/* eslint-disable max-lines */
/* eslint-disable complexity */
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { BenachrichtigungenTrigger } from '@/domain/navigation/benachrichtigungen/components/desktop/BenachrichtigungenTrigger';
import AbwesenheitsantragDetail from '@/domain/navigation/benachrichtigungen/components/details/abwesenheitsantrag/AbwesenheitsantragDetail';
import DokumenteDetail from '@/domain/navigation/benachrichtigungen/components/details/dokumente/DokumenteDetail';
import FehlendeArbeitszeitDetail from '@/domain/navigation/benachrichtigungen/components/details/fehlendeArbeitszeit/FehlendeArbeitszeitDetail';
import FristenDetail from '@/domain/navigation/benachrichtigungen/components/details/fristen/FristenDetail';
import KrankmeldungenDetail from '@/domain/navigation/benachrichtigungen/components/details/krankmeldungen/KrankmeldungenDetail';
import PersonalfragebogenDetail from '@/domain/navigation/benachrichtigungen/components/details/personalfragebogen/PersonalfragebogenDetail';
import SchichttauschDetail from '@/domain/navigation/benachrichtigungen/components/details/schichttausch/SchichttauschDetail';
import { ListItem } from '@/domain/navigation/benachrichtigungen/components/mobile/ListItem/ListItem';
import { SubListItem } from '@/domain/navigation/benachrichtigungen/components/mobile/subListItem/SubListItem';
import { SubListItemEmpty } from '@/domain/navigation/benachrichtigungen/components/mobile/subListItem/SubListItemEmpty';
import { Benachrichtigungen } from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { useProfile } from '@/hooks';
import {
  faChevronLeft,
  faCalendarXmark,
  faClock,
  faXmarkLarge,
  faAlarmExclamation,
  faSuitcaseMedical,
  faFile,
  faPaperPlane,
  faArrowLeftArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type BenachrichtungenOptions =
  | 'fehlendeArbeitszeiten'
  | 'abwesenheitsAntraege'
  | 'fristen'
  | 'krankmeldungen'
  | 'dokumente'
  | 'personalfragebogen'
  | 'schichttausch';

// eslint-disable-next-line max-lines-per-function
export default function BenachrichtungenMobile({
  benachrichtigungen,
}: {
  benachrichtigungen: Benachrichtigungen;
}) {
  const { t } = useTranslation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] =
    useState<BenachrichtungenOptions | null>(null);
  const { manager } = useProfile();

  function handleClose() {
    setIsSheetOpen(false);
    setActiveSubMenu(null);
  }

  return (
    <Sheet
      open={isSheetOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        } else {
          setIsSheetOpen(true);
        }
      }}>
      <SheetTrigger asChild>
        <BenachrichtigungenTrigger
          count={
            benachrichtigungen.abwesenheitsantraege.length +
            benachrichtigungen.fehlendeArbeitszeiten.length +
            benachrichtigungen.fristen.length +
            benachrichtigungen.krankmeldungen.length +
            benachrichtigungen.dokumente.length +
            benachrichtigungen.personalfragebogen.length +
            benachrichtigungen.schichttauschAntraege.length
          }
          active={
            benachrichtigungen.abwesenheitsantraege.length > 0 ||
            benachrichtigungen.fehlendeArbeitszeiten.length > 0 ||
            benachrichtigungen.fristen.length > 0 ||
            benachrichtigungen.krankmeldungen.length > 0 ||
            benachrichtigungen.dokumente.length > 0 ||
            benachrichtigungen.personalfragebogen.length > 0 ||
            benachrichtigungen.schichttauschAntraege.length > 0
          }
        />
      </SheetTrigger>
      <SheetContent className="w-dvw [&>button:first-of-type]:hidden flex flex-col gap-4">
        <SheetHeader className="grid grid-cols-[1fr_auto_1fr] space-y-0 items-center">
          {activeSubMenu && (
            <Button
              variant="ghost"
              className="text-neutrals-N800 "
              size="icon"
              onClick={() => setActiveSubMenu(null)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
          )}
          <span className="col-start-2 w-fit justify-self-center">
            {t('domain.navigation.benachrichtigungen.title')}
          </span>
          <Button
            variant="ghost"
            className="text-neutrals-N800 justify-self-end mt-0"
            size="icon"
            onClick={() => {
              setActiveSubMenu(null);
              setIsSheetOpen(false);
            }}>
            <FontAwesomeIcon icon={faXmarkLarge} />
          </Button>
        </SheetHeader>
        {!activeSubMenu ? (
          <div className="flex flex-col gap-4">
            <ListItem
              onClick={() => setActiveSubMenu('fehlendeArbeitszeiten')}
              count={benachrichtigungen.fehlendeArbeitszeiten.length}>
              <FontAwesomeIcon icon={faClock} />
              <p>
                {t(
                  'domain.navigation.benachrichtigungen.fehlendeArbeitszeiten',
                )}
              </p>
            </ListItem>
            {manager.rechte.kalender && (
              <ListItem
                count={benachrichtigungen.abwesenheitsantraege.length}
                onClick={() => setActiveSubMenu('abwesenheitsAntraege')}>
                <FontAwesomeIcon icon={faCalendarXmark} />
                <p>
                  {t(
                    'domain.navigation.benachrichtigungen.abwesenheitsantraege',
                  )}
                </p>
              </ListItem>
            )}
            {manager.rechte.kalender && (
              <ListItem
                count={benachrichtigungen.krankmeldungen.length}
                onClick={() => setActiveSubMenu('krankmeldungen')}>
                <FontAwesomeIcon icon={faSuitcaseMedical} />
                <p>
                  {t(
                    'domain.navigation.benachrichtigungen.krankmeldungen.title',
                  )}
                </p>
              </ListItem>
            )}
            {manager.rechte.mitarbeiter.fristen && (
              <ListItem
                count={benachrichtigungen.fristen.length}
                onClick={() => setActiveSubMenu('fristen')}>
                <FontAwesomeIcon icon={faAlarmExclamation} />
                <p>{t('domain.navigation.benachrichtigungen.fristen')}</p>
              </ListItem>
            )}
            {manager.rechte.mitarbeiter.dokumente && (
              <ListItem
                count={benachrichtigungen.dokumente.length}
                onClick={() => setActiveSubMenu('dokumente')}>
                <FontAwesomeIcon icon={faFile} />
                <p>{t('domain.navigation.benachrichtigungen.dokumente')}</p>
              </ListItem>
            )}
            {manager.rechte.mitarbeiter.erlaubt && (
              <ListItem
                count={benachrichtigungen.personalfragebogen.length}
                onClick={() => setActiveSubMenu('personalfragebogen')}>
                <FontAwesomeIcon icon={faPaperPlane} />
                <p>
                  {t('domain.navigation.benachrichtigungen.personalfragebogen')}
                </p>
              </ListItem>
            )}
            {manager.rechte.dienstplan.mitarbeiterEinteilen &&
              manager.rechte.dienstplan.bearbeiten && (
                <ListItem
                  count={benachrichtigungen.schichttauschAntraege.length}
                  onClick={() => setActiveSubMenu('schichttausch')}>
                  <FontAwesomeIcon icon={faArrowLeftArrowRight} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.schichttauschAntraege',
                    )}
                  </p>
                </ListItem>
              )}
          </div>
        ) : (
          <Transition
            key={activeSubMenu}
            show={activeSubMenu !== null}
            appear
            enter="transition transform duration-300 ease-in-out"
            enterFrom="translate-x-full opacity-0"
            enterTo="translate-x-0 opacity-100"
            leave="transition transform duration-300 ease-in-out"
            leaveFrom="translate-x-0 opacity-100"
            leaveTo="translate-x-full opacity-0">
            <nav className="px-4 flex flex-col gap-4 overflow-scroll">
              {activeSubMenu === 'fehlendeArbeitszeiten' && (
                <div onClick={() => handleClose()}>
                  {benachrichtigungen.fehlendeArbeitszeiten.length > 0 ? (
                    benachrichtigungen.fehlendeArbeitszeiten.map((b, idx) => (
                      <SubListItem
                        key={b.id}
                        isLast={
                          benachrichtigungen.fehlendeArbeitszeiten.length -
                            1 ===
                          idx
                        }>
                        <FehlendeArbeitszeitDetail
                          benachrichtigung={b}
                          key={b.id}
                        />
                      </SubListItem>
                    ))
                  ) : (
                    <SubListItemEmpty />
                  )}
                </div>
              )}
              {activeSubMenu === 'abwesenheitsAntraege' && (
                <div onClick={() => handleClose()}>
                  {benachrichtigungen.abwesenheitsantraege.length > 0 ? (
                    benachrichtigungen.abwesenheitsantraege.map((b, idx) => (
                      <SubListItem
                        onClick={() => handleClose()}
                        key={b.id}
                        isLast={
                          benachrichtigungen.abwesenheitsantraege.length - 1 ===
                          idx
                        }>
                        <AbwesenheitsantragDetail
                          benachrichtigung={b}
                          key={b.id}
                        />
                      </SubListItem>
                    ))
                  ) : (
                    <SubListItemEmpty />
                  )}
                </div>
              )}
              {activeSubMenu === 'krankmeldungen' && (
                <div onClick={() => handleClose()}>
                  {benachrichtigungen.krankmeldungen.length > 0 ? (
                    benachrichtigungen.krankmeldungen.map((b, idx) => (
                      <SubListItem
                        onClick={() => handleClose()}
                        key={idx}
                        isLast={
                          benachrichtigungen.krankmeldungen.length - 1 === idx
                        }>
                        <KrankmeldungenDetail benachrichtigung={b} />
                      </SubListItem>
                    ))
                  ) : (
                    <SubListItemEmpty />
                  )}
                </div>
              )}
              {activeSubMenu === 'fristen' &&
                (benachrichtigungen.fristen.length > 0 ? (
                  benachrichtigungen.fristen.map((b, idx) => (
                    <SubListItem
                      key={b.id}
                      isLast={benachrichtigungen.fristen.length - 1 === idx}>
                      <FristenDetail benachrichtigung={b} key={b.id} />
                    </SubListItem>
                  ))
                ) : (
                  <SubListItemEmpty />
                ))}
              {activeSubMenu === 'dokumente' &&
                (benachrichtigungen.dokumente.length > 0 ? (
                  benachrichtigungen.dokumente.map((b, idx) => (
                    <SubListItem
                      key={b.id}
                      isLast={benachrichtigungen.dokumente.length - 1 === idx}>
                      <DokumenteDetail benachrichtigung={b} key={b.id} />
                    </SubListItem>
                  ))
                ) : (
                  <SubListItemEmpty />
                ))}
              {activeSubMenu === 'personalfragebogen' &&
                (benachrichtigungen.personalfragebogen.length > 0 ? (
                  benachrichtigungen.personalfragebogen.map((b, idx) => (
                    <SubListItem
                      key={b.id}
                      isLast={
                        benachrichtigungen.personalfragebogen.length - 1 === idx
                      }>
                      <PersonalfragebogenDetail benachrichtigung={b} />
                    </SubListItem>
                  ))
                ) : (
                  <SubListItemEmpty />
                ))}
              {activeSubMenu === 'schichttausch' &&
                (benachrichtigungen.schichttauschAntraege.length > 0 ? (
                  benachrichtigungen.schichttauschAntraege.map((b, idx) => (
                    <SubListItem
                      key={b.id}
                      isLast={
                        benachrichtigungen.schichttauschAntraege.length - 1 ===
                        idx
                      }>
                      <SchichttauschDetail benachrichtigung={b} />
                    </SubListItem>
                  ))
                ) : (
                  <SubListItemEmpty />
                ))}
            </nav>
          </Transition>
        )}
      </SheetContent>
    </Sheet>
  );
}
